import { createSlice } from "@reduxjs/toolkit"
import { Influencer, SocialAccount } from "types/influencer"

export interface UIComponents {
    influencerPostModal: {
        postId: string,
        influencer?: Influencer,
        status?: 'pending' | 'approved' | 'rejected' | 'processing',
        milestoneStep?: string | null,
        socialAccount: SocialAccount
    } | null,
    paymentsOffcanvas: boolean,
    applicationOffcanvas: {
        campaignId: string,
        influencerId: string,
        status: 'pending' | 'accepted' | 'rejected' | 'processing'
        approve?: () => void,
        reject?: () => void
    } | null,
    postPerformanceOffcanvas: {
        collaborationId: string,
    } | null,
    error: {
        code: number,
        message: string
    } | null,
    lastActiveCampaignTab: 'applications' | 'collaborations' | 'insights',
    campaignsLocation: {pathname: string, search?: string},
    notificationPreferences: boolean,
    addCreatorContactModal: boolean,
    updateCreatorProfileModal: {
        influencerId: string
        creatorName?: string
    } | null,
    manageCreatorTagsOffcanvas: boolean,
    handleCreatorTagsModal: {
        socialAccountId: string
        tags: string[]
        creatorName?: string
    } | null,
    addContactMobileModal: {
        socialAccountId: string
        creatorName?: string
    } | null,
    creatorFiltersModal: boolean,
    addPANModal: boolean,
}

const initialState: UIComponents = {
    influencerPostModal : null,
    paymentsOffcanvas: false,
    error: null,
    applicationOffcanvas: null,
    postPerformanceOffcanvas: null,
    lastActiveCampaignTab: 'applications',
    campaignsLocation: {pathname: '/campaign'},
    notificationPreferences: false,
    addCreatorContactModal: false,
    updateCreatorProfileModal: null,
    manageCreatorTagsOffcanvas: false,
    handleCreatorTagsModal: null,
    addContactMobileModal: null,
    creatorFiltersModal: false,
    addPANModal: false,
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        showInfluencerPostModal: (state: UIComponents, action: { payload: { postId: string, influencer?: Influencer, socialAccount: SocialAccount, status?: 'pending' | 'approved' | 'rejected' | 'processing', milestoneStep?: string | null } }) => {
                state.influencerPostModal = action.payload;
            },
        closeInfluencerPostModal: (state: UIComponents) => {
            state.influencerPostModal = null;
        },
        togglePaymentsOffcanvas: (state: UIComponents, action : {payload: boolean}) => {
            state.paymentsOffcanvas = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        toggleApplicationOffcanvas: (state, action : {payload: {campaignId: string, influencerId: string, status: 'pending' | 'accepted' | 'rejected' | 'processing', approve?: () => void, reject?: () => void} | null}) => {
            state.applicationOffcanvas = action.payload;
        },
        togglePostPerformanceOffcanvas: (state, action: {payload: {collaborationId: string} | null}) => {
            state.postPerformanceOffcanvas = action.payload
        },
        setLastActiveCampaignTab : (state, action: {payload: 'applications' | 'collaborations' | 'insights'}) => {
            state.lastActiveCampaignTab = action.payload;
        }, 
        setCampaignsLocation: (state, action: {payload: {pathname: string, search?: string}}) => {
            state.campaignsLocation = action.payload
        },
        toggleNotificationPreferencesModal: (state, {payload}) => {
            state.notificationPreferences = payload;
        },
        toggleAddCreatorContactModal: (state, action: {payload: boolean}) => {
            state.addCreatorContactModal = action.payload;
        },
        toggleUpdateCreatorProfileModal: (state, action: { payload: { influencerId: string; creatorName?: string } | null }) => {
            state.updateCreatorProfileModal = action.payload;
        },
        toggleManageCreatorTagsOffcanvas: (state, action: { payload: boolean }) => {
            state.manageCreatorTagsOffcanvas = action.payload;
        },
        toggleHandleCreatorTagsModal: (state, action: { payload: UIComponents['handleCreatorTagsModal'] }) => {
            state.handleCreatorTagsModal = action.payload;
        },
        toggleAddContactMobileModal: (state, action: { payload: UIComponents['addContactMobileModal'] }) => {
            state.addContactMobileModal = action.payload;
        },
        toggleCreatorFiltersModal: (state, action: { payload: boolean }) => {
            state.creatorFiltersModal = action.payload;
        },
        toggleAddPANModal: (state, action: { payload: boolean }) => {
            state.addPANModal = action.payload;
        },
    }
})

export const {
    showInfluencerPostModal,
    closeInfluencerPostModal,
    togglePaymentsOffcanvas,
    setError,
    toggleApplicationOffcanvas,
    togglePostPerformanceOffcanvas,
    setCampaignsLocation,
    setLastActiveCampaignTab,
    toggleNotificationPreferencesModal,
    toggleAddCreatorContactModal,
    toggleUpdateCreatorProfileModal,
    toggleManageCreatorTagsOffcanvas,
    toggleHandleCreatorTagsModal,
    toggleAddContactMobileModal,
    toggleCreatorFiltersModal,
    toggleAddPANModal,
} = uiSlice.actions;

export default uiSlice.reducer